import { NoneBonus } from "@/models/bonus/NoneBonus";
import { LAST_PITCH_POSITION, teamConstructor } from "@/utils/constructors/teamConstructor";

/**
 * Bonus dont l'effet ne dépend pas de l'adversaire rencontré :
 * le capitaine et la composition sont choisis sans savoir contre qui on tombe.
 * Le remplacement obligatoire n'y figure pas : son malus de poste sera
 * recalculé par applyClassicSubstitutions puisque le remplacement est refait.
 */
const OPPONENT_INDEPENDENT_BONUSES = ["captain", "boostDefense4", "boostDefense5"];

/**
 * Renvoi le bonus de note d'un joueur privé des bonus liés à l'adversaire
 * Sur un match terminé, MPG a déjà appliqué les bonus dans bonusRating et en
 * donne le détail par joueur dans bonusesDetails : on recompose la somme
 * plutôt que d'annuler les bonus un à un.
 * @param {Object} playerData les données d'un joueur du match terminé
 * @returns {Number} le bonus de note conservé
 */
const getKeptBonusRating = (playerData) => {
  return Object.entries(playerData.bonusesDetails || {})
    .filter(([bonusName]) => OPPONENT_INDEPENDENT_BONUSES.includes(bonusName))
    .reduce((total, [, { bonusRating }]) => total + (bonusRating || 0), 0);
};

/**
 * Renvoi les joueurs débarrassés des effets du match réellement joué
 * @param {Object} players les joueurs indexés par identifiant
 * @returns {Object} les joueurs nettoyés
 */
const cleanPlayers = (players) => {
  return Object.fromEntries(Object.entries(players).map(([playerId, playerData]) => [
    playerId,
    {
      ...playerData,
      bonusRating: getKeptBonusRating(playerData),
      mpgGoals: 0,
      canceledGoals: 0,
      savedGoals: 0,
    },
  ]));
};

/**
 * Renvoi les positions de terrain avec leur titulaire d'origine remis en place
 * MPG a déjà appliqué tous les remplacements sur un match terminé, tactiques,
 * obligatoires comme live, et le remplaçant entré reste sur le banc à sa place
 * d'origine. En remettant systématiquement le titulaire d'origine sur le
 * terrain (avec sa note nulle, puisqu'il n'a pas joué), on laisse le pipeline
 * de remplacement de l'application tout rejouer lui-même.
 * Un remplacement tactique déjà joué doit être réévalué, car le retrait du
 * nerf adverse par cleanPlayers peut remonter la note du titulaire au-dessus
 * du seuil de déclenchement. Un historique live doit être rejoué en entier,
 * car le vrai adversaire a pu le tronquer avec Tonton Pat alors que
 * l'adversaire alternatif, lui, n'a aucun bonus.
 * Le banc redevient cohérent sans traitement supplémentaire, sans quoi le
 * remplaçant s'y retrouverait doublé avec sa présence sur le terrain.
 * @param {Object} playersOnPitch les positions indexées par numéro
 * @returns {Object} les positions avec tous les remplacements défaits
 */
const undoSubstitutions = (playersOnPitch) => {
  return Object.fromEntries(Object.entries(playersOnPitch).map(([position, slot]) => {
    if (Number(position) <= LAST_PITCH_POSITION && slot.starterId) {
      return [position, { playerId: slot.starterId }];
    }
    return [position, slot];
  }));
};

/**
 * Renvoi le capitaine s'il est bien sur le terrain
 * @param {Object} teamData les données de l'équipe
 * @param {Object} playersOnPitch les positions avec tous les remplacements défaits
 * @returns {String|undefined} l'identifiant du capitaine
 */
const getPitchCaptain = (teamData, playersOnPitch) => {
  const isOnPitch = Object.entries(playersOnPitch).some(([position, { playerId }]) => {
    return Number(position) <= LAST_PITCH_POSITION && playerId === teamData.captain;
  });
  return isOnPitch ? teamData.captain : undefined;
};

/**
 * Construit une équipe à partir des données d'un match terminé, sans les bonus
 * qui dépendent de l'adversaire rencontré
 * @param {Object} teamData les données d'une équipe dans divisionMatches
 * @returns {Team} l'équipe prête à être recalculée
 */
const finishedTeamConstructor = (teamData) => {
  const playersOnPitch = undoSubstitutions(teamData.playersOnPitch);

  const team = teamConstructor({
    ...teamData,
    tacticalSubs: teamData.tacticalSubs || [],
    liveSubstitutesHistory: teamData.liveSubstitutesHistory || [],
    players: cleanPlayers(teamData.players),
    playersOnPitch,
    captain: getPitchCaptain(teamData, playersOnPitch),
  });

  team.bonus = new NoneBonus();
  team.initialBonus = new NoneBonus();
  team.pitchPlayers.forEach((player) => {
    player.notPlayed = player.rating === undefined;
  });

  return team;
};

export { finishedTeamConstructor };